import { Check } from "lucide-react";

import {
	PROGRAM_COLORS,
	PROGRAM_COLOR_CLASSES,
	normalizeProgramColor,
	programColorLabelKey,
} from "../../lib/programColors";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * La riga di pallini con cui il coach sceglie il colore di un programma:
 * «nessuno» per primo, poi la tavolozza. Un solo clic sceglie, un clic sul
 * pallino scelto non fa niente (si toglie con «nessuno», che e' sempre li').
 *
 * E' un gruppo radio: frecce per muoversi, `aria-checked` sullo scelto, e la
 * scelta e' doppiata da una spunta e dal nome scritto accanto — mai solo
 * colore (WCAG 1.4.1). Bersagli da 40px: il coach la usa anche dal telefono.
 * Usato da creazione, modifica rapida e Dettagli del builder: un solo
 * selettore, lo stesso ovunque. Spec: docs/program-colors/01-analisi.md.
 */
export function ProgramColorPicker({ value, onChange, className = "" }) {
	const { t } = useLanguage();
	const selected = normalizeProgramColor(value);
	const options = [null, ...PROGRAM_COLORS];

	const labelOf = (color) =>
		color ? t(programColorLabelKey(color)) : t("programColorNone");

	const move = (event, index) => {
		const step =
			event.key === "ArrowRight" || event.key === "ArrowDown"
				? 1
				: event.key === "ArrowLeft" || event.key === "ArrowUp"
					? -1
					: 0;
		if (!step) return;
		event.preventDefault();
		const next = options[(index + step + options.length) % options.length];
		onChange(next);
		event.currentTarget.parentElement
			?.querySelector(`[data-color="${next ?? "none"}"]`)
			?.focus();
	};

	return (
		<div className={`flex items-center gap-2 flex-wrap ${className}`}>
			<div
				role="radiogroup"
				aria-label={t("programColor")}
				className="flex items-center flex-wrap gap-0.5"
				data-testid="program-color-picker"
			>
				{options.map((color, index) => {
					const isSelected = selected === color;
					const classes = color ? PROGRAM_COLOR_CLASSES[color] : null;
					return (
						<button
							key={color ?? "none"}
							type="button"
							role="radio"
							aria-checked={isSelected}
							aria-label={labelOf(color)}
							title={labelOf(color)}
							tabIndex={isSelected ? 0 : -1}
							data-color={color ?? "none"}
							onClick={() => onChange(color)}
							onKeyDown={(event) => move(event, index)}
							className="h-10 w-10 inline-flex items-center justify-center rounded-sm hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/60"
						>
							<span
								className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
									classes
										? classes.bg
										: "border border-dashed border-muted-foreground/60"
								} ${
									isSelected
										? `ring-2 ring-offset-2 ring-offset-card ${classes ? classes.ring : "ring-muted-foreground"}`
										: ""
								}`}
							>
								{isSelected && (
									<Check
										className={`w-3 h-3 ${classes ? "text-black/80" : "text-muted-foreground"}`}
										strokeWidth={3}
										aria-hidden="true"
									/>
								)}
							</span>
						</button>
					);
				})}
			</div>
			<span className="text-xs text-muted-foreground" aria-live="polite">
				{labelOf(selected)}
			</span>
		</div>
	);
}
